/**
 * DL-02 — the "Downloads" modal (opened from the pill): every download of
 * this tab — running, queued, stopped, done — ONE CARD each (Christian
 * 2026-10-22: "a card for each element"): the scope on the first line with
 * its state chip (D81 slot recipe: queued → neutral, running → info,
 * stopped / failed → attention, done → success), the folder and the counts
 * under it, a progress bar while it runs, and the row's actions as compact
 * buttons — Stop (running / queued), Resume (stopped / failed), Remove.
 * The list scrolls inside the dialog (twenty queued sets must not grow the
 * modal past the viewport); closing drops the rows that finished cleanly.
 * The cards sit on the canvas surface (`background.default`) inside the
 * raised dialog paper — the elevation ladder's "panel / card" level, which
 * is what separates them from the modal visually (Christian 2026-10-22).
 * Whole-queue actions: Pause all / Resume all / Stop all (a bad hotel line:
 * pause, do something else, resume later).
 *
 * Density: a dialog is portaled to <body>, outside the shell's compact
 * `<main>`, so the paper declares the compact surface itself (D77: density
 * is set ONCE on a region and inherited — the modal is that region).
 */
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import type { ReactElement } from "react";
import { DENSITY_ATTR, statusChipStyle, type StatusSlot } from "@aeriosiq/ui";
import { useDownloadManager } from "./DownloadManagerProvider.js";
import type { DownloadItem, DownloadItemState } from "./manager.js";
import { WORDS } from "./words.js";

export interface DownloadManagerDialogProps {
  open: boolean;
  onClose: () => void;
}

export function DownloadManagerDialog({
  open,
  onClose,
}: DownloadManagerDialogProps): ReactElement {
  const m = useDownloadManager();
  const close = (): void => {
    m.clearDone();
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      aria-labelledby="dl-manager-title"
      data-testid="dl-manager"
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ [DENSITY_ATTR]: "compact" }}
    >
      <DialogTitle id="dl-manager-title">{WORDS.managerTitle}</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "60vh" }}>
        {m.items.length === 0 ? (
          <Typography variant="body2" data-testid="dl-manager-empty">
            {WORDS.managerEmpty}
          </Typography>
        ) : (
          <Stack
            component="ul"
            spacing={1.5}
            sx={{ listStyle: "none", m: 0, p: 0 }}
            data-testid="dl-manager-list"
          >
            {m.items.map((it) => (
              <ItemCard key={it.id} item={it} />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, gap: 0.5 }}>
        {m.paused && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flex: 1, minWidth: 0 }}
            data-testid="dl-manager-paused-hint"
          >
            {WORDS.pausedHint}
          </Typography>
        )}
        {m.totals.active > 0 && !m.paused && (
          <Button
            size="small"
            variant="outlined"
            onClick={m.pauseAll}
            data-testid="dl-manager-pause-all"
          >
            {WORDS.pauseAll}
          </Button>
        )}
        {m.paused && (
          <Button
            size="small"
            variant="contained"
            onClick={m.resumeAll}
            data-testid="dl-manager-resume-all"
          >
            {WORDS.resumeAll}
          </Button>
        )}
        {(m.totals.active > 0 || m.totals.paused > 0) && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={m.stopAll}
            data-testid="dl-manager-stop-all"
          >
            {WORDS.stopAll}
          </Button>
        )}
        <Button
          size="small"
          variant="text"
          onClick={close}
          data-testid="dl-manager-close"
        >
          {WORDS.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Item state → D81 slot (the meaning carries the colour, never the call site). */
function slotOf(state: DownloadItemState, failures: number): StatusSlot {
  switch (state) {
    case "queued":
    case "paused":
      return "neutral";
    case "running":
      return "info";
    case "stopped":
      return "attention";
    case "done":
      return failures > 0 ? "attention" : "success";
  }
}

function StateChip({
  state,
  failures,
}: {
  state: DownloadItemState;
  failures: number;
}): ReactElement {
  const theme = useTheme();
  const style = statusChipStyle(theme, slotOf(state, failures));
  const label = WORDS.itemState(state, failures);
  return (
    <Chip
      label={label}
      size="small"
      variant="filled"
      icon={
        <Box
          component="span"
          aria-hidden
          sx={{
            width: 8,
            height: 8,
            borderRadius: "999px",
            backgroundColor: style.dotColor,
            flexShrink: 0,
          }}
        />
      }
      sx={{
        backgroundColor: style.backgroundColor,
        color: style.color,
        flexShrink: 0,
        fontWeight: 500,
        "& .MuiChip-label": { color: style.color },
        "& .MuiChip-icon": { marginInlineStart: "8px", marginInlineEnd: 0 },
      }}
      data-state={state}
      aria-label={`Status: ${label}`}
    />
  );
}

function ItemCard({ item }: { item: DownloadItem }): ReactElement {
  const m = useDownloadManager();
  const p = item.progress;
  const done = p?.filesDone ?? 0;
  const total = p?.filesTotal ?? item.bundle.imageCount;
  const failures = p?.failures.length ?? 0;
  const resumable =
    item.state === "stopped" ||
    item.state === "paused" ||
    (item.state === "done" && failures > 0);
  const busy = item.state === "running" || item.state === "queued";
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const first = item.bundle.manifests[0];
  const folder =
    item.bundle.manifests.length === 1 && first
      ? `${item.root.name} / ${first.folderName}`
      : `${item.root.name} · ${String(item.bundle.manifests.length)} data sets`;
  return (
    <Card
      component="li"
      variant="outlined"
      sx={{ bgcolor: "background.default" }}
      data-testid={`dl-item-${item.id}`}
      data-state={item.state}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: 500, minWidth: 0 }}
            >
              {item.scope.label ?? WORDS.title(item.scope)}
            </Typography>
            <StateChip state={item.state} failures={failures} />
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap>
            {folder}
          </Typography>
          {item.state === "running" && (
            <LinearProgress
              variant={p?.verified ? "determinate" : "indeterminate"}
              value={pct}
              aria-label={WORDS.progressLabel}
              data-testid={`dl-item-${item.id}-progress`}
            />
          )}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              variant="caption"
              sx={{ fontVariantNumeric: "tabular-nums", minWidth: 0 }}
              noWrap
              data-testid={`dl-item-${item.id}-line`}
            >
              {WORDS.itemLine(
                item.state,
                done,
                total,
                p?.bytesWritten ?? 0,
                failures,
              )}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              {busy && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    m.stop(item.id);
                  }}
                  data-testid={`dl-item-${item.id}-stop`}
                >
                  {WORDS.stop}
                </Button>
              )}
              {resumable && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    m.resume(item.id);
                  }}
                  data-testid={`dl-item-${item.id}-resume`}
                >
                  {WORDS.resume}
                </Button>
              )}
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  m.remove(item.id);
                }}
                data-testid={`dl-item-${item.id}-remove`}
              >
                {WORDS.remove}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
